<script lang="ts">
    import type { Appointment } from '../../../modules/schedulerApi/appointments/appointment'
    import type { Schedule } from '../../../modules/schedulerApi/schedules/schedule'
    import AppointmentView from './appointment-view.svelte'
    import dayjs from 'dayjs'
    import _isToday from 'dayjs/plugin/isToday'
    dayjs.extend(_isToday)
    import { onDestroy, onMount, tick } from 'svelte'
    import { AppointmentStatus } from '../../../modules/schedulerApi/appointments/appointments.enum'
    import 'dayjs/locale/pt-br'
    dayjs.locale('pt-br')
    //dayjs.extend(utc)
    import { ScheduleSlot, ScheduleSlotStatus } from '../../../modules/schedulerApi/schedules/scheduleSlot'
    import ScheduleByColumnSlot from './schedule-by-column-slot.svelte'
    import ScheduleColumnHeader from './schedule-column-header.svelte'
    import { createEventDispatcher } from 'svelte'
    const dispatch = createEventDispatcher()

    export let showTime: boolean = true
    export let schedule: Schedule
    export let index: number = 0
    export let heightPerMinute = 0
    export let start: number
    export let end: number
    export let blink: string[]
    export let groups: { scheduleGroupId: string; name: string }[] = []
    export let fullWidth: boolean = false
    // "Largura padrão" da agenda (1x/2x) — o container só repassa na visão diária; o CSS só aplica no computador
    export let widthFactor: number = 1

    let height = 2000 // to force css position:sticky to work
    let now = 0
    let nowThread = null
    let firstTime = null
    let slots = []
    let items: { position: number; shared: number; appointment: Appointment }[] = []

    let isWeeklyToday = false
    function updateNow() {
        if (nowThread) clearTimeout(nowThread)
        if (!dayjs(schedule.selectedDate).isToday()) {
            isWeeklyToday = false
            now = 0
            return
        }
        isWeeklyToday = !schedule.label
        now = (new Date().getHours() * 60 + new Date().getMinutes() - start) * heightPerMinute
        nowThread = setTimeout(() => {
            updateNow()
        }, 60 * 1000)
        let current = new Date().getHours() * 60 + new Date().getMinutes()
        if (current > end + 30) now = 0
    }

    onMount(async () => {})

    $: {
        if (heightPerMinute > 0) {
            loadSchedule(schedule)
        }
    }

    onDestroy(() => {
        if (nowThread) clearTimeout(nowThread)
    })

    function DatePlusMinutes(minutes: number) {
        let dateStart = new Date()
        if (schedule.selectedDate) dateStart = new Date(schedule.selectedDate)
        dateStart.setHours(0)
        dateStart.setSeconds(0)
        dateStart.setMinutes(minutes)
        return dateStart
    }

    async function loadSlots(s: Schedule) {
        let current = start
        slots = []
        await tick()
        // primeiro equipara o início da agenda com os demais
        if (current < s.startMinute) {
            let slot = new ScheduleSlot()
            slot.start = DatePlusMinutes(current)
            slot.end = DatePlusMinutes(s.startMinute)
            slot.status = ScheduleSlotStatus.agendamento_desativado
            slot.scheduleId = s.scheduleId
            slots.push(slot)
            current = s.startMinute
        }
        // cria os slots da agenda atual
        while (current < s.endMinute) {
            let slot = new ScheduleSlot()
            slot.start = DatePlusMinutes(current)
            slot.end = DatePlusMinutes(current + s.range)
            slot.scheduleId = s.scheduleId
            if (s.slots) {
                while (true) {
                    const currentSlot = getNextScheduleSlot(s, current)
                    if (!currentSlot) break
                    if (currentSlot.active) break
                    slot.status = ScheduleSlotStatus.agendamento_desativado
                    slot.end = DatePlusMinutes(currentSlot.end)
                    current += s.range
                }
            }
            if (!slot.status) {
                slot.status = current < s.startMinute ? ScheduleSlotStatus.agendamento_bloqueado : ScheduleSlotStatus.agendamento_disponivel
                current += s.range
            }
            slots.push(slot)
        }
        // equipara o final da agenda com as demais agendas
        if (current < end) {
            let slot = new ScheduleSlot()
            slot.start = DatePlusMinutes(current)
            slot.end = DatePlusMinutes(end)
            slot.status = ScheduleSlotStatus.agendamento_desativado
            slot.scheduleId = s.scheduleId
            slots.push(slot)
            current = end
        }
        height = (s.endMinute - s.startMinute) * heightPerMinute
        slots = slots
        updateNow()
    }

    function getNextScheduleSlot(s: Schedule, startTime: number) {
        if (!s.selectedDate) {
            return
        }
        const currentDay = s.slots[s.selectedDate.getDay()]
        const currentSlot = currentDay.slots.find((el) => el.start === startTime)
        return currentSlot
    }

    async function loadSchedule(s: Schedule) {
        firstTime = start
        await loadSlots(s)
        items = []
        for (let appointment of s.appointments) {
            appointment.start = dayjs(appointment.start).toDate()
            appointment.end = dayjs(appointment.end).toDate()
            let item: { position: number; shared: number; appointment: Appointment } = {
                position: 1,
                shared: 1,
                appointment: appointment,
            }
            items.push(item)
        }
        for (let me of items) {
            for (let other of items) {
                if (me.appointment.appointmentId === other.appointment.appointmentId) break
                other.appointment.start.setMilliseconds(0)
                other.appointment.end.setMilliseconds(0)
                if (me.appointment.start < other.appointment.end && me.appointment.start >= other.appointment.start) {
                    me.position += 1
                    if (me.position === other.position) {
                        me.position = 1
                    }
                }
            }
        }
        // faz os encaixes
        for (let me of items) {
            let check = false
            for (let other of items) {
                if (me.appointment.appointmentId === other.appointment.appointmentId) {
                    check = true
                    continue
                }
                if (check) {
                    if (me.position < other.position) {
                        me.shared = other.position
                        other.shared = other.position
                    }
                    if (me.position > other.position) {
                        if (me.appointment.end > other.appointment.start) {
                            other.shared = me.position
                        }
                        break
                    }
                    if (me.position == other.position) {
                        break
                    }
                }
            }
        }
        items = items
        updateNow()
    }

    let selecting = false
    let selectedFirst = null
    function startSelecting(event) {
        dispatch('startSelecting', schedule)
        selecting = true
        selectedFirst = event.detail
    }

    export let clearSelection = null
    export let selecitonRange = { start: null, end: null, scheduleId: null, auto: false }
    function stopSelecting(event) {
        if (selecting) {
            let selectedLast = event.detail
            selecitonRange.scheduleId = schedule.scheduleId
            if (selectedFirst.start <= selectedLast.start) {
                selecitonRange.start = selectedFirst.start
                selecitonRange.end = selectedLast.end
            } else {
                selecitonRange.start = selectedLast.start
                selecitonRange.end = selectedFirst.end
            }
            selecitonRange.auto =
                selectedFirst.start.getHours() + selectedFirst.start.getMinutes() === selectedLast.start.getHours() + selectedLast.start.getMinutes()
            selectedFirst = null
            selecting = false
            dispatch('stopSelecting', selecitonRange)
        }
    }

    function onClick(ev) {
        dispatch('click')
    }

    function goPaste(ev) {
        dispatch('paste', ev.detail)
    }
    let clear = false
    $: {
        clear = clearSelection?.scheduleId !== schedule.scheduleId || clearSelection.selectedDate !== schedule.selectedDate
    }

    function editAppointment(ev) {
        dispatch('edit', ev.detail)
    }
    function editBlock(ev) {
        dispatch('edit-block', ev.detail)
    }

    function onInvoice(event) {
        dispatch('invoice', event.detail)
    }
</script>

<div class="list schedule-col -fullWidth-{fullWidth}" class:-width-2={widthFactor === 2} style="min-height:{height}px">
    <ScheduleColumnHeader {schedule} {groups} today={isWeeklyToday} />
    <div class="list-body">
        {#each slots as slot}
            <ScheduleByColumnSlot
                {slot}
                {selecting}
                {clear}
                {showTime}
                {heightPerMinute}
                openFrom={schedule.startMinute}
                openTo={schedule.endMinute}
                on:startSelecting={startSelecting}
                on:stopSelecting={stopSelecting}
                on:paste={goPaste}
                on:click={onClick}
            />
        {/each}
        {#each items as item (item.appointment.appointmentId)}
            <AppointmentView
                blink={blink?.includes(item.appointment?.appointmentId)}
                {item}
                {clear}
                {showTime}
                {heightPerMinute}
                {firstTime}
                on:edit={editAppointment}
                on:edit-block={editBlock}
                on:invoice={onInvoice}
            />
        {/each}
        {#if now > 0}
            <div class="now" style="top:{now}px">
                {#if index === 0}
                    <div class="now-dot" />
                {/if}
            </div>
        {/if}
    </div>
</div>

<style>
    .schedule-col {
        display: flex;
        flex-direction: column;
        flex: 1 1 350px;
        max-width: 350px;
        min-width: 173px;
        /* margin-right: 8px; */
        border-right: 1px solid #202020;
        position: relative;
    }
    .list-body {
        border: 0px dashed #99999922;
        flex-grow: 1;
        border-radius: 8px;
        position: relative;
    }
    .now {
        background: rgb(46, 204, 113, 200);
        height: 2px;
        position: absolute;
        z-index: 1;
        margin-left: -1px;
        width: calc(100% + 1px);
        pointer-events: none;
    }
    .now-dot {
        height: 8px;
        width: 8px;
        border-radius: 50%;
        background-color: rgb(46, 204, 113);
        margin-top: -4px;
        margin-left: -4px;
        pointer-events: none;
    }

    /* agenda com "Largura padrão" 2x: o dobro da base, do máximo e do mínimo de uma coluna comum.
       só no computador — no celular cada coluna já ocupa a largura da tela */
    @media (min-width: 901px) {
        .schedule-col.-width-2 {
            flex: 2 2 700px;
            max-width: 700px;
            min-width: 346px;
        }
    }

    @media (max-width: 900px) {
        .schedule-col {
            /* box-shadow: 1px 1px 6px #111; */
            border-radius: 0px;
            /* overflow: hidden; */
            margin-right: 0px;
            /* max-width: 450px; */
            min-width: 340px;
        }
        .schedule-col.-fullWidth-true {
            max-width: unset;
        }

        /* .list-header {
            position: relative;
            display: none;
        } */
        .list-body {
            margin-top: 0px;
        }
        .now {
            display: none;
        }
    }

    @media print {
        .list {
            min-height: unset !important;
            display: flex;
            flex-direction: column;
            width: 100%;
            max-width: unset;
            min-width: unset;
            border-right: 0px solid #202020;
            position: relative;
        }

        .now {
            display: none;
        }
    }
</style>
